import { useMemo } from 'react';

import { useFeatureGate } from '@trello/feature-gate-client';

export interface PlannerFeatureGates {
  areSubscriptionsEnabled: boolean;
  isPlannerCacheFirstFF: boolean;
  isZenModeEnabled: boolean;
  isAutoUpdatingDateEnabled: boolean;
  isSingleCardEventEnabled: boolean;
  isPlannerRealtimeUpdatesEnabled: boolean;
  isPlannerOutlookEnabled: boolean;
  isPlannerEventPreloadingEnabled: boolean;
  isScheduleFromCardQuickActionsEnabled: boolean;
}

/**
 * Hook to determine access to various planner features.
 *
 * @returns {PlannerFeatureGates} An object containing boolean flags for different planner features
 */
export function usePlannerFeatureGate(): PlannerFeatureGates {
  const { value: subscriptionsGate } = useFeatureGate(
    'xf_planner_card_subscriptions',
  );
  const { value: cacheFirstGate } = useFeatureGate('xf_planner_cache_first');
  const { value: zenModeGate } = useFeatureGate('phx_zen_mode');
  const { value: singleCardEventGate } = useFeatureGate(
    'xf_planner_single_card_event',
  );
  const { value: autoUpdatingDateGate } = useFeatureGate(
    'xf_planner_auto_updating_date',
  );
  const { value: realtimeUpdatesGate } = useFeatureGate(
    'xf_planner_realtime_updates',
  );
  const { value: plannerOutlook } = useFeatureGate('xf_planner_outlook');
  const { value: plannerEventPreloading } = useFeatureGate(
    'xf_planner_event_preloading',
  );
  const { value: scheduleFromCardQuickActions } = useFeatureGate(
    'xf_planner_card_quick_actions_scheduler',
  );

  return useMemo(
    () => ({
      areSubscriptionsEnabled: subscriptionsGate,
      isPlannerCacheFirstFF: cacheFirstGate,
      isZenModeEnabled: zenModeGate,
      isSingleCardEventEnabled: singleCardEventGate,
      isAutoUpdatingDateEnabled: autoUpdatingDateGate,
      isPlannerRealtimeUpdatesEnabled: realtimeUpdatesGate,
      isPlannerOutlookEnabled: plannerOutlook,
      isPlannerEventPreloadingEnabled: plannerEventPreloading,
      isScheduleFromCardQuickActionsEnabled: scheduleFromCardQuickActions,
    }),
    [
      subscriptionsGate,
      cacheFirstGate,
      zenModeGate,
      singleCardEventGate,
      autoUpdatingDateGate,
      realtimeUpdatesGate,
      plannerOutlook,
      plannerEventPreloading,
      scheduleFromCardQuickActions,
    ],
  );
}
